import React, { useState } from 'react';
import { Phone, MapPin, Mail, Star, Users, Award, Clock, MessageCircle, Calendar, ChevronRight, BookOpen, GraduationCap, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';

const AstrologyWebsite = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    age: '',
    gender: '',
    dateOfBirth: '',
    timeOfBirth: '',
    placeOfBirth: '',
    address: '',
    state: '',
    services: [],
    consultationType: '',
    preferredDate: '',
    specialRequests: '',
    agreed: false
  });

  const services = [
    {
      id: 'janmakundali',
      title: 'Janmakundali Analysis',
      titleHi: 'जन्मकुण्डली विश्लेषण',
      price: '₹1500',
      duration: '45-60 minutes',
      description: 'Complete birth chart analysis for life predictions and guidance',
      descriptionHi: 'जीवन की भविष्यवाणी और मार्गदर्शन के लिए संपूर्ण जन्म चार्ट विश्लेषण'
    },
    {
      id: 'matrimonial',
      title: 'Matrimonial Horoscope Matching',
      titleHi: 'वैवाहिक कुण्डली मिलान',
      price: '₹1200',
      duration: '30-45 minutes',
      description: 'Comprehensive compatibility analysis for marriage proposals',
      descriptionHi: 'विवाह प्रस्तावों के लिए व्यापक संगतता विश्लेषण'
    },
    {
      id: 'palmistry',
      title: 'Palm Reading',
      titleHi: 'हस्तरेखा परीक्षण',
      price: '₹800',
      duration: '30 minutes',
      description: 'Complete palmistry analysis for life, career and health predictions',
      descriptionHi: 'जीवन, करियर और स्वास्थ्य की भविष्यवाणी के लिए संपूर्ण हस्तरेखा विश्लेषण'
    },
    {
      id: 'vastu',
      title: 'Vastu Consultation',
      titleHi: 'वास्तु परामर्श',
      price: '₹5000',
      duration: '60-90 minutes',
      description: 'Home and office Vastu analysis for prosperity and harmony',
      descriptionHi: 'समृद्धि और सामंजस्य के लिए घर और कार्यालय वास्तु विश्लेषण'
    },
    {
      id: 'career',
      title: 'Career & Business Guidance',
      titleHi: 'करियर और व्यापार मार्गदर्शन',
      price: '₹1000',
      duration: '45 minutes',
      description: 'Astrological guidance for career growth and business success',
      descriptionHi: 'करियर की वृद्धि और व्यापारिक सफलता के लिए ज्योतिषीय मार्गदर्शन'
    },
    {
      id: 'remedial',
      title: 'Remedial Solutions',
      titleHi: 'दोष निवारण',
      price: '₹600',
      duration: '30 minutes',
      description: 'Gemstone recommendations, mantras and remedies for astrological doshas',
      descriptionHi: 'ज्योतिषीय दोषों के लिए रत्न सुझाव, मंत्र और उपचार'
    }
  ];

  const stats = [
    { icon: Award, value: '30+', label: 'Years Experience', labelHi: 'वर्षों का अनुभव' },
    { icon: Users, value: '10K+', label: 'Happy Clients', labelHi: 'खुश ग्राहक' },
    { icon: Star, value: '95%', label: 'Accuracy Rate', labelHi: 'सटीकता दर' },
    { icon: Clock, value: '24/7', label: 'Support Available', labelHi: 'सहायता उपलब्ध' }
  ];

  const testimonials = [
    {
      name: 'Priya Sharma',
      location: 'Delhi',
      text: 'Acharya ji\'s predictions were remarkably accurate. His guidance helped me make the right career decision.',
      textHi: 'आचार्य जी की भविष्यवाणियां बेहद सटीक थीं। उनके मार्गदर्शन ने मुझे सही करियर निर्णय लेने में मदद की।'
    },
    {
      name: 'Rahul Gupta',
      location: 'Ghaziabad',
      text: 'Excellent Vastu consultation! Our business has grown significantly after implementing his suggestions.',
      textHi: 'उत्कृष्ट वास्तु परामर्श! उनके सुझावों को लागू करने के बाद हमारा व्यापार काफी बढ़ा है।'
    },
    {
      name: 'Sunita Agarwal',
      location: 'Noida',
      text: 'His palmistry reading was incredibly detailed and insightful. Highly recommended!',
      textHi: 'उनकी हस्तरेखा परीक्षा अविश्वसनीय रूप से विस्तृत और अंतर्दृष्टिपूर्ण थी। अत्यधिक अनुशंसित!'
    }
  ];

  const handleServiceToggle = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter(id => id !== serviceId)
        : [...prev.services, serviceId]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // WhatsApp redirect with booking details to notification number
    const selectedServices = formData.services.map(serviceId => {
      const service = services.find(s => s.id === serviceId);
      return service ? `${service.title} (${service.price})` : serviceId;
    }).join(', ');

    const message = `🔔 New Consultation Booking\n\n👤 Name: ${formData.fullName}\n📞 Phone: ${formData.phone}\n${formData.email ? `📧 Email: ${formData.email}\n` : ''}${formData.dateOfBirth ? `🎂 DOB: ${formData.dateOfBirth}\n` : ''}📋 Services: ${selectedServices}\n📅 Preferred Date: ${formData.preferredDate || 'Not specified'}\n💬 Consultation Type: ${formData.consultationType || 'Not specified'}\n${formData.specialRequests ? `✍️ Special Requests: ${formData.specialRequests}` : ''}`;

    const whatsappUrl = `https://wa.me/917210416827?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-yellow-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-gradient-to-r from-red-900 via-orange-800 to-red-900 text-white shadow-lg">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="text-center md:text-left mb-4 md:mb-0">
              <h1 className="text-2xl md:text-3xl font-bold">भारतीय ज्योतिष परामर्श</h1>
              <p className="text-sm md:text-base opacity-90">Bharatiya Jyotish Paramarsh</p>
            </div>
            <div className="flex flex-wrap gap-2 md:gap-4 justify-center">
              <Button
                onClick={() => scrollToSection('biography')}
                variant="outline"
                className="bg-white/10 hover:bg-white/20 text-white border-white/30"
              >
                About | परिचय
              </Button>
              <Button
                onClick={() => scrollToSection('services')}
                variant="outline"
                className="bg-white/10 hover:bg-white/20 text-white border-white/30"
              >
                Services | सेवाएं
              </Button>
              <Button
                onClick={() => scrollToSection('booking')}
                variant="outline"
                className="bg-white/10 hover:bg-white/20 text-white border-white/30"
              >
                Book Now | बुक करें
              </Button>
              <Button
                onClick={() => window.open('https://wa.me/919955482922', '_blank')}
                className="bg-green-600 hover:bg-green-700"
              >
                <MessageCircle className="mr-2 h-4 w-4" />
                WhatsApp
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-red-900/10 via-orange-800/10 to-yellow-800/10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-block bg-gradient-to-r from-red-900 to-orange-800 text-white px-4 py-2 rounded-full text-sm mb-6">
              Trusted by 10,000+ clients | 10,000+ ग्राहकों द्वारा विश्वसनीय
            </div>
            <h2 className="text-4xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-red-900 via-orange-800 to-red-900 bg-clip-text text-transparent">
              Expert Astrology Consultation
            </h2>
            <p className="text-2xl md:text-3xl text-gray-700 mb-4">
              विशेषज्ञ ज्योतिष परामर्श
            </p>
            <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Get authentic astrological guidance from <strong className="text-red-900">Acharya Indrajeet Shastri</strong>.
              Experience traditional Vedic astrology with modern convenience.
            </p>
            <p className="text-base md:text-lg text-gray-600 mb-8">
              आचार्य इन्द्रजीत शास्त्री के साथ प्रामाणिक ज्योतिषीय परामर्श प्राप्त करें
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button
                onClick={() => scrollToSection('booking')}
                size="lg"
                className="bg-gradient-to-r from-red-900 to-orange-800 hover:from-red-800 hover:to-orange-700 text-white shadow-lg"
                data-testid="hero-book-consultation-btn"
              >
                <Calendar className="mr-2 h-5 w-5" />
                Book Consultation | परामर्श बुक करें
              </Button>
              <Button
                onClick={() => window.open('https://wa.me/919955482922', '_blank')}
                size="lg"
                variant="outline"
                className="border-2 border-green-600 text-green-600 hover:bg-green-50"
                data-testid="hero-whatsapp-btn"
              >
                <MessageCircle className="mr-2 h-5 w-5" />
                Chat on WhatsApp
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-red-900 via-orange-800 to-red-900 text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <stat.icon className="h-12 w-12 mx-auto mb-4 opacity-90" />
                <div className="text-4xl md:text-5xl font-bold mb-2">{stat.value}</div>
                <div className="text-sm md:text-base opacity-90">{stat.label}</div>
                <div className="text-xs md:text-sm opacity-75">{stat.labelHi}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Astrologer Profile */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <Card className="max-w-4xl mx-auto border-2 border-orange-200 shadow-xl">
            <CardHeader className="bg-gradient-to-r from-red-900 to-orange-800 text-white text-center py-8">
              <div className="w-32 h-32 mx-auto mb-4 bg-white rounded-full flex items-center justify-center text-6xl font-bold text-red-900 shadow-lg">
                आ
              </div>
              <CardTitle className="text-3xl md:text-4xl mb-2">Acharya Indrajeet Shastri</CardTitle>
              <p className="text-xl md:text-2xl mb-2">आचार्य इन्द्रजीत शास्त्री</p>
              <CardDescription className="text-white/90 text-base md:text-lg">
                Vedic Astrologer & Vastu Consultant | वैदिक ज्योतिषी और वास्तु सलाहकार
              </CardDescription>
            </CardHeader>
            <CardContent className="p-8">
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <Award className="h-8 w-8 mx-auto mb-2 text-red-900" />
                  <div className="font-bold text-2xl text-red-900">30+ Years</div>
                  <div className="text-sm text-gray-600">Experience | अनुभव</div>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <Users className="h-8 w-8 mx-auto mb-2 text-red-900" />
                  <div className="font-bold text-2xl text-red-900">10,000+</div>
                  <div className="text-sm text-gray-600">Clients Served | ग्राहक</div>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <Star className="h-8 w-8 mx-auto mb-2 text-red-900" />
                  <div className="font-bold text-2xl text-red-900">4.9/5</div>
                  <div className="text-sm text-gray-600">Rating | रेटिंग</div>
                </div>
              </div>
              <blockquote className="text-center text-gray-700 italic text-lg border-l-4 border-red-900 pl-4 py-2">
                "Providing authentic astrological guidance based on traditional Vedic principles with modern understanding since 1994. Formally educated from renowned Gurukuls with 30+ years of professional experience."
              </blockquote>
              <p className="text-center text-gray-600 mt-2">
                "1994 से पारंपरिक वैदिक सिद्धांतों के आधार पर प्रामाणिक ज्योतिषीय मार्गदर्शन। प्रसिद्ध गुरुकुलों से औपचारिक शिक्षा के साथ 30+ वर्षों का पेशेवर अनुभव।"
              </p>
              <div className="flex flex-wrap gap-4 justify-center mt-6">
                <Button
                  onClick={() => window.open('https://wa.me/919955482922', '_blank')}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="profile-whatsapp-btn"
                >
                  <MessageCircle className="mr-2 h-4 w-4" />
                  Start WhatsApp Chat
                </Button>
                <Button
                  onClick={() => window.open('tel:+917210416827')}
                  variant="outline"
                  className="border-red-900 text-red-900 hover:bg-red-50"
                  data-testid="profile-call-btn"
                >
                  <Phone className="mr-2 h-4 w-4" />
                  Call for Booking
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Biography Section */}
      <section id="biography" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-red-900 to-orange-800 bg-clip-text text-transparent">
              About Acharya ji | आचार्य जी के बारे में
            </h2>
            <p className="text-lg md:text-xl text-gray-600">
              Know Your Spiritual Guide | अपने आध्यात्मिक मार्गदर्शक को जानें
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <Card className="border-2 border-orange-200 shadow-xl overflow-hidden">
              <div className="grid md:grid-cols-2 gap-0">
                {/* Left Side - Image */}
                <div className="bg-gradient-to-br from-red-900 via-orange-800 to-red-900 p-8 flex items-center justify-center">
                  <div className="text-center text-white">
                    <div className="w-64 h-64 mx-auto mb-6 overflow-hidden rounded-lg shadow-2xl border-4 border-white">
                      <img
                        src="https://customer-assets.emergentagent.com/job_palm-mystic/artifacts/osjebzgw_image.png"
                        alt="Acharya Indrajeet Shastri"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h3 className="text-3xl font-bold mb-2">आचार्य इन्द्रजीत शास्त्री</h3>
                    <p className="text-xl mb-4">Acharya Indrajeet Shastri</p>
                    <div className="flex items-center justify-center gap-2 mb-4">
                      <Star className="h-5 w-5 fill-current text-yellow-400" />
                      <span className="text-lg">30+ Years of Experience</span>
                      <Star className="h-5 w-5 fill-current text-yellow-400" />
                    </div>
                    <p className="text-sm opacity-90">वेदिक ज्योतिषी और वास्तु सलाहकार</p>
                    <p className="text-xs opacity-75 mt-2">Vedic Astrologer & Vastu Consultant</p>
                  </div>
                </div>

                {/* Right Side - Bio Content */}
                <div className="p-8">
                  <CardContent className="p-0">
                    <div className="space-y-6">
                      {/* Introduction */}
                      <div>
                        <h4 className="text-2xl font-bold text-red-900 mb-3 flex items-center gap-2">
                          <Sparkles className="h-6 w-6" />
                          Introduction | परिचय
                        </h4>
                        <p className="text-gray-700 leading-relaxed mb-2">
                          Acharya Indrajeet Shastri is a renowned Vedic astrologer with over <strong>30+ years of professional experience</strong> since 1994. With advanced degrees in Sanskrit and Vedic sciences, he combines traditional wisdom with modern understanding to guide thousands of individuals through life's challenges.
                        </p>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          आचार्य इन्द्रजीत शास्त्री एक प्रसिद्ध वैदिक ज्योतिषी हैं जो 1994 से <strong>30+ वर्षों से पेशेवर रूप से कार्यरत</strong> हैं। संस्कृत और वैदिक विज्ञान में उच्च डिग्री के साथ, वे पारंपरिक ज्ञान और आधुनिक समझ को मिलाकर हजारों लोगों का मार्गदर्शन करते हैं।
                        </p>
                      </div>

                      {/* Educational Background */}
                      <div>
                        <h4 className="text-xl font-bold text-red-900 mb-3 flex items-center gap-2">
                          <GraduationCap className="h-5 w-5" />
                          Educational Background | शैक्षिक पृष्ठभूमि
                        </h4>
                        <div className="space-y-3">
                          <div className="bg-orange-50 p-3 rounded-lg border-l-4 border-red-900">
                            <p className="font-semibold text-gray-800">दयानन्द ब्रह्म महाविद्यालय, हिसार, हरियाणा</p>
                            <p className="text-sm text-gray-700">Dayanand Brahm Mahavidyalaya, Hisar, Haryana</p>
                            <p className="text-sm text-gray-600 mt-1"><strong>1983 - 1987:</strong> विद्या राज, विद्या निधि, विद्या वाचस्पति</p>
                            <p className="text-xs text-gray-500">Bachelor's degrees in Vedic Sciences</p>
                          </div>
                          <div className="bg-orange-50 p-3 rounded-lg border-l-4 border-red-900">
                            <p className="font-semibold text-gray-800">गुरुकुल महाविद्यालय, ज्वालापुर, हरिद्वार</p>
                            <p className="text-sm text-gray-700">Gurukul Mahavidyalaya, Jwalapur, Haridwar</p>
                            <p className="text-sm text-gray-600 mt-1"><strong>1987 - 1989:</strong> विद्या भास्कर की डिग्री</p>
                            <p className="text-xs text-gray-500">Master's degree in Vedic Sciences</p>
                          </div>
                        </div>
                      </div>

                      {/* Professional Journey */}
                      <div>
                        <h4 className="text-xl font-bold text-red-900 mb-3 flex items-center gap-2">
                          <BookOpen className="h-5 w-5" />
                          Professional Journey | व्यावसायिक यात्रा
                        </h4>
                        <div className="space-y-2">
                          <div className="flex gap-3">
                            <div className="text-red-900 font-bold min-w-[100px]">1989 - 1994</div>
                            <div>
                              <p className="text-gray-700 font-medium">संस्कृत एवं धर्म शिक्षक</p>
                              <p className="text-sm text-gray-600">Sanskrit & Dharma Teacher</p>
                              <p className="text-xs text-gray-500">D.A.V. School, Thermal Power Colony, Panipat</p>
                            </div>
                          </div>
                          <div className="flex gap-3">
                            <div className="text-red-900 font-bold min-w-[100px]">1994 - Present</div>
                            <div>
                              <p className="text-gray-700 font-medium">ज्योतिष कार्यालय संचालन</p>
                              <p className="text-sm text-gray-600">Running Astrology Consultation Office</p>
                              <p className="text-xs text-gray-500"><strong>30+ years</strong> of dedicated service in Vedic Astrology</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Expertise */}
                      <div>
                        <h4 className="text-xl font-bold text-red-900 mb-3 flex items-center gap-2">
                          <Sparkles className="h-5 w-5" />
                          Areas of Expertise | विशेषज्ञता के क्षेत्र
                        </h4>
                        <ul className="grid grid-cols-1 gap-2">
                          <li className="flex items-start gap-2">
                            <span className="text-red-900 mt-1">✦</span>
                            <div>
                              <span className="text-gray-700 font-medium">Vedic Astrology & Janmakundali Analysis</span>
                              <span className="text-gray-600 text-sm block">वैदिक ज्योतिष और जन्मकुण्डली विश्लेषण</span>
                            </div>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-red-900 mt-1">✦</span>
                            <div>
                              <span className="text-gray-700 font-medium">Palmistry & Hand Reading</span>
                              <span className="text-gray-600 text-sm block">हस्तरेखा विज्ञान</span>
                            </div>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-red-900 mt-1">✦</span>
                            <div>
                              <span className="text-gray-700 font-medium">Vastu Shastra Consultation</span>
                              <span className="text-gray-600 text-sm block">वास्तु शास्त्र परामर्श</span>
                            </div>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-red-900 mt-1">✦</span>
                            <div>
                              <span className="text-gray-700 font-medium">Marriage Compatibility & Kundali Matching</span>
                              <span className="text-gray-600 text-sm block">विवाह संगतता और कुण्डली मिलान</span>
                            </div>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-red-900 mt-1">✦</span>
                            <div>
                              <span className="text-gray-700 font-medium">Career & Business Guidance</span>
                              <span className="text-gray-600 text-sm block">करियर और व्यापार मार्गदर्शन</span>
                            </div>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-red-900 mt-1">✦</span>
                            <div>
                              <span className="text-gray-700 font-medium">Gemstone & Remedial Solutions</span>
                              <span className="text-gray-600 text-sm block">रत्न और उपचार समाधान</span>
                            </div>
                          </li>
                        </ul>
                      </div>

                      {/* Philosophy */}
                      <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border-2 border-orange-200">
                        <h4 className="text-lg font-bold text-red-900 mb-3">Philosophy & Approach | दर्शन और दृष्टिकोण</h4>
                        <blockquote className="border-l-4 border-red-900 pl-4 py-2 italic text-gray-700">
                          "With formal education from renowned Gurukuls and 30+ years of practical experience, I combine traditional Vedic wisdom with modern life understanding to provide authentic and practical astrological guidance."
                        </blockquote>
                        <p className="text-gray-600 text-sm mt-2 italic">
                          "प्रसिद्ध गुरुकुलों से औपचारिक शिक्षा और 30+ वर्षों के व्यावहारिक अनुभव के साथ, मैं प्रामाणिक और व्यावहारिक ज्योतिषीय मार्गदर्शन प्रदान करने के लिए पारंपरिक वैदिक ज्ञान को आधुनिक जीवन की समझ के साथ जोड़ता हूं।"
                        </p>
                      </div>

                      {/* Achievements */}
                      <div className="bg-gradient-to-r from-red-900 to-orange-800 p-4 rounded-lg text-white">
                        <h4 className="text-lg font-bold mb-3">Key Achievements | मुख्य उपलब्धियां</h4>
                        <div className="grid grid-cols-4 gap-3 text-center">
                          <div>
                            <div className="text-2xl font-bold">30+</div>
                            <div className="text-xs opacity-90">Years<br />वर्ष</div>
                          </div>
                          <div>
                            <div className="text-2xl font-bold">10,000+</div>
                            <div className="text-xs opacity-90">Consultations<br />परामर्श</div>
                          </div>
                          <div>
                            <div className="text-2xl font-bold">95%</div>
                            <div className="text-xs opacity-90">Accuracy<br />सटीकता</div>
                          </div>
                          <div>
                            <div className="text-2xl font-bold">4.9/5</div>
                            <div className="text-xs opacity-90">Rating<br />रेटिंग</div>
                          </div>
                        </div>
                      </div>

                      {/* Call to Action */}
                      <div className="flex gap-3">
                        <Button
                          onClick={() => scrollToSection('booking')}
                          className="flex-1 bg-gradient-to-r from-red-900 to-orange-800 hover:from-red-800 hover:to-orange-700"
                          data-testid="bio-book-btn"
                        >
                          <Calendar className="mr-2 h-4 w-4" />
                          Book Consultation
                        </Button>
                        <Button
                          onClick={() => window.open('https://wa.me/919955482922', '_blank')}
                          className="flex-1 bg-green-600 hover:bg-green-700"
                          data-testid="bio-whatsapp-btn"
                        >
                          <MessageCircle className="mr-2 h-4 w-4" />
                          Chat Now
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16 bg-gradient-to-br from-orange-50 to-red-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-red-900 to-orange-800 bg-clip-text text-transparent">
              Our Services | हमारी सेवाएं
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive Astrology Services | संपूर्ण ज्योतिष सेवाएं
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <Card
                key={service.id}
                className="hover:shadow-xl transition-shadow border-2 border-orange-200 bg-white"
                data-testid={`service-card-${service.id}`}
              >
                <CardHeader className="bg-gradient-to-r from-red-900 to-orange-800 text-white">
                  <CardTitle className="text-xl">{service.title}</CardTitle>
                  <CardDescription className="text-white/90">{service.titleHi}</CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-3xl font-bold text-red-900">{service.price}</span>
                    <span className="text-sm text-gray-600">{service.duration}</span>
                  </div>
                  <p className="text-gray-700 mb-2">{service.description}</p>
                  <p className="text-gray-600 text-sm mb-4">{service.descriptionHi}</p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => scrollToSection('booking')}
                      className="flex-1 bg-gradient-to-r from-red-900 to-orange-800 hover:from-red-800 hover:to-orange-700"
                      data-testid={`book-${service.id}-btn`}
                    >
                      Book Now
                    </Button>
                    <Button
                      onClick={() => window.open('https://wa.me/919955482922', '_blank')}
                      variant="outline"
                      className="border-green-600 text-green-600 hover:bg-green-50"
                      data-testid={`whatsapp-${service.id}-btn`}
                    >
                      <MessageCircle className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-red-900 to-orange-800 bg-clip-text text-transparent">
              Client Reviews | ग्राहक समीक्षा
            </h2>
            <p className="text-lg md:text-xl text-gray-600">
              What Our Clients Say | हमारे ग्राहक क्या कहते हैं
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-red-50">
                <CardContent className="pt-6">
                  <div className="flex mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-yellow-500 fill-current" />
                    ))}
                  </div>
                  <blockquote className="text-gray-700 mb-2 italic">"{testimonial.text}"</blockquote>
                  <p className="text-gray-600 text-sm mb-4">"{testimonial.textHi}"</p>
                  <div className="font-bold text-red-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-600">{testimonial.location}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section id="booking" className="py-16 bg-gradient-to-br from-orange-50 to-red-50">
        <div className="container mx-auto px-4">
          <Card className="max-w-4xl mx-auto border-2 border-orange-200 shadow-xl">
            <CardHeader className="bg-gradient-to-r from-red-900 to-orange-800 text-white text-center">
              <CardTitle className="text-3xl md:text-4xl">Book Your Consultation</CardTitle>
              <CardDescription className="text-white/90 text-lg">
                अपना परामर्श बुक करें
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 md:p-8">
              <form onSubmit={handleSubmit} data-testid="booking-form">
                {/* Personal Information */}
                <div className="mb-8">
                  <h3 className="text-xl font-bold mb-4 text-red-900">Personal Information | व्यक्तिगत जानकारी</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="fullName">Full Name | पूरा नाम *</Label>
                      <Input
                        id="fullName"
                        required
                        value={formData.fullName}
                        onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                        data-testid="input-fullname"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone Number | फोन नंबर *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        data-testid="input-phone"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address | ईमेल</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        data-testid="input-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="age">Age | आयु</Label>
                      <Input
                        id="age"
                        type="number"
                        value={formData.age}
                        onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                        data-testid="input-age"
                      />
                    </div>
                  </div>
                </div>

                {/* Birth Information */}
                <div className="mb-8">
                  <h3 className="text-xl font-bold mb-4 text-red-900">Birth Information | जन्म विवरण</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="dateOfBirth">Date of Birth | जन्म तिथि</Label>
                      <Input
                        id="dateOfBirth"
                        type="date"
                        value={formData.dateOfBirth}
                        onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                        data-testid="input-dob"
                      />
                    </div>
                    <div>
                      <Label htmlFor="timeOfBirth">Time of Birth | जन्म समय</Label>
                      <Input
                        id="timeOfBirth"
                        type="time"
                        value={formData.timeOfBirth}
                        onChange={(e) => setFormData({ ...formData, timeOfBirth: e.target.value })}
                        data-testid="input-tob"
                      />
                    </div>
                    <div>
                      <Label htmlFor="placeOfBirth">Place of Birth | जन्म स्थान</Label>
                      <Input
                        id="placeOfBirth"
                        value={formData.placeOfBirth}
                        onChange={(e) => setFormData({ ...formData, placeOfBirth: e.target.value })}
                        data-testid="input-pob"
                      />
                    </div>
                  </div>
                </div>

                {/* Services Selection */}
                <div className="mb-8">
                  <h3 className="text-xl font-bold mb-4 text-red-900">Select Services | सेवा चयन *</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {services.map((service) => (
                      <div key={service.id} className="flex items-start space-x-3 p-4 border-2 border-orange-200 rounded-lg hover:bg-orange-50">
                        <Checkbox
                          id={service.id}
                          checked={formData.services.includes(service.id)}
                          onCheckedChange={() => handleServiceToggle(service.id)}
                          data-testid={`checkbox-${service.id}`}
                        />
                        <label htmlFor={service.id} className="flex-1 cursor-pointer">
                          <div className="font-semibold">{service.title}</div>
                          <div className="text-sm text-gray-600">{service.titleHi}</div>
                          <div className="text-sm font-bold text-red-900">{service.price} - {service.duration}</div>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Consultation Type */}
                <div className="mb-8">
                  <h3 className="text-xl font-bold mb-4 text-red-900">Consultation Preferences | परामर्श प्राथमिकताएं</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="consultationType">Consultation Type | परामर्श प्रकार *</Label>
                      <Select
                        value={formData.consultationType}
                        onValueChange={(value) => setFormData({ ...formData, consultationType: value })}
                      >
                        <SelectTrigger data-testid="select-consultation-type">
                          <SelectValue placeholder="Select consultation type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="online">Online Video Call | ऑनलाइन वीडियो कॉल</SelectItem>
                          <SelectItem value="phone">Phone Consultation | फोन परामर्श</SelectItem>
                          <SelectItem value="inperson">In-Person Visit | व्यक्तिगत भेंट</SelectItem>
                          <SelectItem value="whatsapp">WhatsApp Chat | व्हाट्सएप चैट</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="preferredDate">Preferred Date | पसंदीदा तारीख</Label>
                      <Input
                        id="preferredDate"
                        type="date"
                        value={formData.preferredDate}
                        onChange={(e) => setFormData({ ...formData, preferredDate: e.target.value })}
                        data-testid="input-preferred-date"
                      />
                    </div>
                  </div>
                </div>

                {/* Special Requests */}
                <div className="mb-8">
                  <Label htmlFor="specialRequests">Special Requests or Questions | विशेष अनुरोध</Label>
                  <Textarea
                    id="specialRequests"
                    rows={4}
                    value={formData.specialRequests}
                    onChange={(e) => setFormData({ ...formData, specialRequests: e.target.value })}
                    data-testid="textarea-special-requests"
                  />
                </div>

                {/* Terms Agreement */}
                <div className="flex items-start space-x-3 mb-6">
                  <Checkbox
                    id="agreed"
                    checked={formData.agreed}
                    onCheckedChange={(checked) => setFormData({ ...formData, agreed: checked })}
                    data-testid="checkbox-terms"
                  />
                  <label htmlFor="agreed" className="text-sm text-gray-600 cursor-pointer">
                    I agree to the terms and conditions and privacy policy. I understand that consultation fees are non-refundable.
                    | मैं नियमों और शर्तों से सहमत हूं।
                  </label>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  size="lg"
                  disabled={!formData.agreed || formData.services.length === 0}
                  className="w-full bg-gradient-to-r from-red-900 to-orange-800 hover:from-red-800 hover:to-orange-700 text-lg"
                  data-testid="submit-booking-btn"
                >
                  Submit Booking | बुकिंग सबमिट करें
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>

                <p className="text-center text-sm text-gray-600 mt-4">
                  After submitting, you'll be redirected to WhatsApp for immediate confirmation
                  | सबमिट करने के बाद आपको तुरंत व्हाट्सएप पर भेज दिया जाएगा
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-red-900 to-orange-800 bg-clip-text text-transparent">
              Contact Us | संपर्क करें
            </h2>
            <p className="text-lg md:text-xl text-gray-600">
              Get In Touch | संपर्क में रहें
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <Card className="border-2 border-orange-200 hover:shadow-xl transition-shadow">
              <CardContent className="pt-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-red-900 to-orange-800 rounded-full flex items-center justify-center">
                  <MessageCircle className="h-8 w-8 text-white" />
                </div>
                <h3 className="font-bold text-lg mb-2">WhatsApp Consultation</h3>
                <p className="text-sm text-gray-600 mb-2">व्हाट्सएप परामर्श</p>
                <p className="font-bold text-red-900 mb-2">+91 99554 82922</p>
                <p className="text-sm text-gray-600 mb-2">(Chat with Acharya ji)</p>
                <p className="font-bold text-red-900 mb-2">+91 72104 16827</p>
                <p className="text-sm text-gray-600 mb-4">(Booking & Notifications)</p>
                <Button
                  onClick={() => window.open('https://wa.me/919955482922', '_blank')}
                  className="w-full bg-green-600 hover:bg-green-700"
                  data-testid="contact-whatsapp-btn"
                >
                  Chat with Acharya ji
                </Button>
              </CardContent>
            </Card>

            <Card className="border-2 border-orange-200 hover:shadow-xl transition-shadow">
              <CardContent className="pt-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-red-900 to-orange-800 rounded-full flex items-center justify-center">
                  <Phone className="h-8 w-8 text-white" />
                </div>
                <h3 className="font-bold text-lg mb-2">Phone Consultation</h3>
                <p className="text-sm text-gray-600 mb-2">फोन परामर्श</p>
                <p className="font-bold text-red-900 mb-4">+91 72104 16827</p>
                <Button
                  onClick={() => window.open('tel:+917210416827')}
                  className="w-full bg-gradient-to-r from-red-900 to-orange-800 hover:from-red-800 hover:to-orange-700"
                  data-testid="contact-call-btn"
                >
                  Call Now
                </Button>
              </CardContent>
            </Card>

            <Card className="border-2 border-orange-200 hover:shadow-xl transition-shadow">
              <CardContent className="pt-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-red-900 to-orange-800 rounded-full flex items-center justify-center">
                  <MapPin className="h-8 w-8 text-white" />
                </div>
                <h3 className="font-bold text-lg mb-2">Office Visit</h3>
                <p className="text-sm text-gray-600 mb-2">कार्यालय भेंट</p>
                <p className="font-bold text-red-900 mb-4">
                  Vasundhara Sector 3<br />
                  Ghaziabad, Uttar Pradesh
                </p>
                <Button
                  onClick={() => window.open('https://maps.google.com/?q=Vasundhara+Sector+3+Ghaziabad', '_blank')}
                  variant="outline"
                  className="w-full border-red-900 text-red-900 hover:bg-red-50"
                  data-testid="contact-directions-btn"
                >
                  Get Directions
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Office Hours */}
          <Card className="max-w-2xl mx-auto mt-8 border-2 border-orange-200">
            <CardHeader className="bg-gradient-to-r from-red-900 to-orange-800 text-white text-center">
              <CardTitle>Office Hours | कार्यालय समय</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <div className="font-bold text-red-900 mb-2">Monday - Saturday</div>
                  <div className="text-gray-600">9:00 AM - 7:00 PM</div>
                </div>
                <div>
                  <div className="font-bold text-red-900 mb-2">Sunday</div>
                  <div className="text-gray-600">10:00 AM - 4:00 PM</div>
                </div>
                <div className="col-span-2">
                  <div className="font-bold text-red-900 mb-2">Online Consultations</div>
                  <div className="text-gray-600">Available 24/7 | 24/7 उपलब्ध</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-red-900 via-orange-800 to-red-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">भारतीय ज्योतिष परामर्श</h3>
              <p className="text-white/80">Bharatiya Jyotish Paramarsh</p>
              <p className="text-white/80 mt-2">आचार्य इन्द्रजीत शास्त्री</p>
              <p className="text-white/80">Acharya Indrajeet Shastri</p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Quick Links | त्वरित लिंक</h4>
              <ul className="space-y-2">
                <li>
                  <button onClick={() => scrollToSection('services')} className="text-white/80 hover:text-white">
                    Services | सेवाएं
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('booking')} className="text-white/80 hover:text-white">
                    Book Consultation | बुक करें
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Contact | संपर्क</h4>
              <div className="space-y-2 text-white/80">
                <p>📱 +91 99554 82922 (WhatsApp)</p>
                <p>📱 +91 72104 16827 (Booking)</p>
                <p>📍 Vasundhara Sector 3, Ghaziabad</p>
              </div>
            </div>
          </div>
          <div className="border-t border-white/20 pt-8 text-center">
            <p className="text-white/80">
              © 2025 Bharatiya Jyotish Paramarsh. All rights reserved. | सर्वाधिकार सुरक्षित।
            </p>
            <p className="text-white/60 text-sm mt-2">www.jyotishparamarsh.com</p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <Button
        onClick={() => window.open('https://wa.me/919955482922', '_blank')}
        className="fixed bottom-8 right-8 w-16 h-16 rounded-full bg-green-600 hover:bg-green-700 shadow-2xl z-50 p-0"
        data-testid="floating-whatsapp-btn"
      >
        <MessageCircle className="h-8 w-8" />
      </Button>
    </div>
  );
};

export default AstrologyWebsite;
